import React, { useEffect, useState } from "react";
import { Button, Form, InputNumber, Select, Alert, Spin, Tag, message } from "antd";

import SmartRtpLayout from "./components/SmartRtpLayout";
import SmartRtpCards from "./components/SmartRtpCards";
import {
  getGamesList,
  predictPrizeDistribution,
} from "../../../controllers/V1/smartRtp/smartRtpController";
import { formatSmartRtpNumber } from "../../../utils/bigMath";

const CATEGORY_STYLE = {
  Low: { label: "LOW", color: "#198754", bg: "#e8f5ee", bar: "#198754" },
  Medium: { label: "MEDIUM", color: "#fd7e14", bg: "#fff3e8", bar: "#fd7e14" },
  High: { label: "HIGH", color: "#dc3545", bg: "#fdecee", bar: "#dc3545" },
};

function formatNum(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  const n = Number(value);
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(digits);
}

function categoryTag(category) {
  const style = CATEGORY_STYLE[category] || CATEGORY_STYLE.Medium;
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: `${style.color}55`,
        fontWeight: 700,
        marginInlineEnd: 0,
      }}
    >
      {style.label}
    </Tag>
  );
}

function ShareBar({ percent, color }) {
  const width = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <div style={{ minWidth: 120 }}>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "#e9ecef",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${width}%`,
            height: "100%",
            background: color,
            borderRadius: 999,
            transition: "width 0.2s ease",
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}>
        {formatNum(percent, 2)}%
      </div>
    </div>
  );
}

function rowBackground(category) {
  const style = CATEGORY_STYLE[category];
  return style ? style.bg : undefined;
}

export default function SmartRtpPredictDistributionPage() {
  const [form] = Form.useForm();
  const [gameOptions, setGameOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Crown King || Smart RTP Predict Distribution";
    const loadGames = async () => {
      const res = await getGamesList({
        type: "",
        condition: { status: "A" },
        limit: 0,
        skip: 0,
        sort: { title: 1 },
      });
      if (res.status === true) {
        const options = (res.result || []).map((item) => ({
          label: `${item.title} (${item.game_code})`,
          value: item._id,
        }));
        setGameOptions(options);
        if (options.length === 1) {
          form.setFieldsValue({ game_id: options[0].value });
        }
      }
    };
    loadGames();
  }, [form]);

  const onPredict = async (values) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await predictPrizeDistribution({
        game_id: values.game_id,
        order_count: values.order_count,
        tickets_per_order: values.tickets_per_order,
        simulations: values.simulations,
        starting_loss_streak: values.starting_loss_streak || 0,
        starting_compassion_remaining: values.starting_compassion_remaining || 0,
        include_timeline: values.simulations === 1,
      });
      if (res.status !== true) {
        message.error(res.message || "Prediction failed.");
        return;
      }
      setResult(res.result);
    } finally {
      setLoading(false);
    }
  };

  const summary = result?.summary;
  const cards = summary
    ? [
        { key: "wins", label: "Avg Wins", value: formatNum(summary.avg_wins) },
        {
          key: "win-rate",
          label: "Win Rate %",
          value: formatNum(summary.win_rate_percent),
        },
        {
          key: "reward",
          label: "Avg Total Reward",
          value: formatSmartRtpNumber(summary.avg_total_reward),
        },
        {
          key: "rtp",
          label: "Projected RTP %",
          value: formatNum(summary.avg_projected_rtp_percent),
        },
        {
          key: "compassion",
          label: "Avg Compassion Wins",
          value: formatNum(summary.avg_compassion_wins),
        },
        {
          key: "natural",
          label: "Avg Natural Wins",
          value: formatNum(summary.avg_natural_wins),
        },
        {
          key: "unpaid",
          label: "Avg Unpaid Wins",
          value: formatNum(summary.avg_unpaid_wins),
          helper: "BRE Win but no slab/pool payout",
        },
        {
          key: "stake",
          label: "Avg Total Stake",
          value: formatSmartRtpNumber(summary.avg_total_stake),
        },
      ]
    : [];

  const categoryRows = result?.category_distribution || [];
  const slabRows = result?.slab_distribution || [];

  return (
    <SmartRtpLayout title="Smart RTP Predict Distribution" activeKey="predict" headerVariant="simple">
      <div className="card shadow mb-4">
        <div className="card-body">
          <p className="text-muted mb-3">
            Simulate prize outcomes for <strong>X orders</strong> using the
            current Settings, Buyer Reward rule, pool balance, and slabs. This
            does not create real orders.
          </p>
          <Form
            form={form}
            layout="vertical"
            onFinish={onPredict}
            initialValues={{
              order_count: 20,
              tickets_per_order: 1,
              simulations: 1,
              starting_loss_streak: 0,
              starting_compassion_remaining: 0,
            }}
          >
            <div className="row">
              <div className="col-md-4">
                <Form.Item
                  name="game_id"
                  label="Game"
                  rules={[{ required: true, message: "Select a game." }]}
                >
                  <Select
                    options={gameOptions}
                    placeholder="Select game"
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </div>
              <div className="col-md-2">
                <Form.Item
                  name="order_count"
                  label="Orders (X)"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={10000} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div className="col-md-2">
                <Form.Item name="tickets_per_order" label="Tickets / Order">
                  <InputNumber min={1} max={20} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div className="col-md-2">
                <Form.Item
                  name="simulations"
                  label="Simulations"
                  tooltip="1 = one sample timeline. Higher = average distribution."
                >
                  <InputNumber min={1} max={200} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div className="col-md-2">
                <Form.Item name="starting_loss_streak" label="Start Loss Streak">
                  <InputNumber min={0} max={100} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div className="col-md-2">
                <Form.Item
                  name="starting_compassion_remaining"
                  label="Compassion left"
                  tooltip="Starting compassion window remaining for this simulated buyer."
                >
                  <InputNumber min={0} max={50} style={{ width: "100%" }} />
                </Form.Item>
              </div>
            </div>
            <Button type="primary" htmlType="submit" loading={loading}>
              Predict Distribution
            </Button>
          </Form>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spin />
        </div>
      ) : null}

      {result ? (
        <>
          {(result.warnings || []).length > 0 ? (
            <div className="mb-3">
              {result.warnings.map((warning) => (
                <Alert
                  key={warning}
                  type="warning"
                  showIcon
                  message={warning}
                  className="mb-2"
                />
              ))}
            </div>
          ) : null}

          <div className="card shadow mb-4">
            <div className="card-body">
              <h6 className="mb-3">Active config snapshot</h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th>Game</th>
                      <td>
                        {result.config?.game_title} ({result.config?.game_code})
                      </td>
                      <th>Ticket price</th>
                      <td>{formatSmartRtpNumber(result.config?.ticket_price)}</td>
                    </tr>
                    <tr>
                      <th>Pool balance</th>
                      <td>{formatSmartRtpNumber(result.config?.pool_balance)}</td>
                      <th>Compassion</th>
                      <td>
                        {result.config?.enable_consecutive_loss === "Y"
                          ? `On — threshold ${result.config?.consecutive_loss_threshold}, window ${result.config?.compassion_window_orders}`
                          : "Off"}
                      </td>
                    </tr>
                    <tr>
                      <th>Cooldown</th>
                      <td>{result.config?.win_cooldown_orders}</td>
                      <th>Consolation only</th>
                      <td>{result.config?.consolation_only ? "Yes" : "No"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <SmartRtpCards cards={cards} />

          <div className="card shadow mb-4">
            <div className="card-body">
              <h6 className="mb-3">Category distribution (Low / Medium / High)</h6>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                {["Low", "Medium", "High"].map((key) => {
                  const style = CATEGORY_STYLE[key];
                  const row =
                    categoryRows.find((item) => item.prize_category === key) || {
                      count: 0,
                      share_percent: 0,
                      avg_reward: 0,
                    };
                  return (
                    <div
                      key={key}
                      style={{
                        background: style.bg,
                        border: `1px solid ${style.color}33`,
                        borderLeft: `4px solid ${style.color}`,
                        borderRadius: 8,
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.6,
                          color: "#6c757d",
                          marginBottom: 4,
                        }}
                      >
                        {style.label}
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: style.color,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatNum(row.share_percent, 2)}%
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: "#495057" }}>
                        Hits: <strong>{row.count || 0}</strong>
                      </div>
                      <div style={{ marginTop: 2, fontSize: 13, color: "#495057" }}>
                        Avg reward:{" "}
                        <strong style={{ color: style.color }}>
                          {formatSmartRtpNumber(row.avg_reward || 0)} QR
                        </strong>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="table-responsive">
                <table className="table table-bordered mb-0" width="100%">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Total hits</th>
                      <th>Avg / run</th>
                      <th>Share of wins</th>
                      <th>Avg reward (QR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.map((row) => {
                      const style =
                        CATEGORY_STYLE[row.prize_category] || CATEGORY_STYLE.Medium;
                      return (
                        <tr
                          key={row.prize_category}
                          style={{ background: rowBackground(row.prize_category) }}
                        >
                          <td>{categoryTag(row.prize_category)}</td>
                          <td>{row.count}</td>
                          <td>{formatNum(row.avg_per_run)}</td>
                          <td>
                            <ShareBar percent={row.share_percent} color={style.bar} />
                          </td>
                          <td>{formatSmartRtpNumber(row.avg_reward)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card shadow mb-4">
            <div className="card-body pt-0">
              <h6 className="pt-3 mb-3">Slab distribution</h6>
              <div className="mb-2 text-muted small">
                Color pattern:{" "}
                <Tag color="success">LOW</Tag>
                <Tag color="orange">MEDIUM</Tag>
                <Tag color="error">HIGH</Tag>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered" width="100%">
                  <thead>
                    <tr>
                      <th>Sl. No.</th>
                      <th>Category</th>
                      <th>Slot</th>
                      <th>Slab</th>
                      <th>Prize (QR)</th>
                      <th>Weight %</th>
                      <th>Total hits</th>
                      <th>Avg / run</th>
                      <th>Share of wins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slabRows.length > 0 ? (
                      slabRows.map((row, index) => {
                        const style =
                          CATEGORY_STYLE[row.prize_category] || CATEGORY_STYLE.Medium;
                        return (
                          <tr
                            key={`${row.slab_code}-${index}`}
                            style={{ background: rowBackground(row.prize_category) }}
                          >
                            <td>{index + 1}</td>
                            <td>{categoryTag(row.prize_category)}</td>
                            <td>{row.slot_index != null ? `#${row.slot_index}` : "-"}</td>
                            <td>
                              <div style={{ fontWeight: 600 }}>
                                {row.slab_name || row.slab_code}
                              </div>
                              <div className="text-muted small">{row.slab_code}</div>
                            </td>
                            <td>{formatSmartRtpNumber(row.fixed_amount)}</td>
                            <td>{formatSmartRtpNumber(row.base_selection_weight)}%</td>
                            <td>{row.count}</td>
                            <td>{formatNum(row.avg_per_run)}</td>
                            <td>
                              <ShareBar percent={row.share_percent} color={style.bar} />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-3">
                          No winning slabs in this simulation.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {result.sample_run?.timeline ? (
            <div className="card shadow mb-4">
              <div className="card-body pt-0">
                <h6 className="pt-3 mb-3">Sample order timeline</h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm" width="100%">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Outcome</th>
                        <th>p_final</th>
                        <th>Streak</th>
                        <th>Compassion left</th>
                        <th>Slab</th>
                        <th>Reward</th>
                        <th>Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample_run.timeline.map((row) => (
                        <tr
                          key={row.order_index}
                          className={row.is_win ? "table-success" : undefined}
                        >
                          <td>{row.order_index}</td>
                          <td>
                            {row.outcome}
                            {row.compassion_forced ? " (compassion)" : ""}
                          </td>
                          <td>{formatNum(row.p_final, 4)}</td>
                          <td>{row.loss_streak}</td>
                          <td>{row.compassion_remaining}</td>
                          <td>{row.slab_code || "-"}</td>
                          <td>{formatSmartRtpNumber(row.reward_amount)}</td>
                          <td>{formatSmartRtpNumber(row.remaining_prize_budget)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </SmartRtpLayout>
  );
}
